import type { StockItemView } from '@/lib/stocksApi'

// ── Medication columns ─────────────────────────────────────────────

const MEDICATION_COLUMNS: { stockType: string; label: string }[] = [
  { stockType: 'MED_OXYTOCINE',    label: 'Oxytocine' },
  { stockType: 'MED_MGSO4',        label: 'MgSO\u2084' },
  { stockType: 'MED_AMOXICILLINE', label: 'Amoxicilline' },
  { stockType: 'MED_PARACETAMOL',  label: 'Parac\u00E9tamol' },
  { stockType: 'MED_SERUM_GLUCOS', label: 'S\u00E9rum glucos\u00E9' },
  { stockType: 'MED_MISOPROSTOL',  label: 'Misoprostol' },
]

// ── Status badge ───────────────────────────────────────────────────

function StatusBadge({ item }: { item: StockItemView | undefined }) {
  if (!item) {
    return <span className="text-neutral-300">&mdash;</span>
  }

  const cls =
    item.status === 'CRITICAL'
      ? 'bg-red-100 text-red-800 border border-red-300'
      : item.status === 'WARNING'
      ? 'bg-orange-100 text-orange-800 border border-orange-300'
      : 'bg-emerald-50 text-emerald-700'

  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-mono font-medium ${cls}`}
    >
      {item.quantity}/{item.minQuantity}
    </span>
  )
}

// ── MedicationStockTable ───────────────────────────────────────────

interface MedicationStockTableProps {
  items: StockItemView[]
}

export function MedicationStockTable({ items }: MedicationStockTableProps) {
  // 1. Collect unique structures sorted by name
  const structureMap = new Map<string, { id: string; name: string }>()
  for (const item of items) {
    if (!structureMap.has(item.structureId)) {
      structureMap.set(item.structureId, {
        id: item.structureId,
        name: item.structureName,
      })
    }
  }
  const structures = Array.from(structureMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name, 'fr')
  )

  // 2. Build lookup: structureId → stockType → StockItemView
  const lookup = new Map<string, Map<string, StockItemView>>()
  for (const item of items) {
    if (!lookup.has(item.structureId)) {
      lookup.set(item.structureId, new Map())
    }
    lookup.get(item.structureId)!.set(item.stockType, item)
  }

  if (structures.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-neutral-400">
        Aucune donnée de stock médicament disponible.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-100 bg-neutral-50">
            <th className="px-4 py-2.5 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide whitespace-nowrap">
              Structure
            </th>
            {MEDICATION_COLUMNS.map((col) => (
              <th
                key={col.stockType}
                className="px-3 py-2.5 text-center text-xs font-medium text-neutral-500 uppercase tracking-wide whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {structures.map((structure) => {
            const byType = lookup.get(structure.id)
            return (
              <tr key={structure.id} className="hover:bg-neutral-50 transition-colors">
                <td className="px-4 py-2.5 text-xs font-medium text-neutral-700 whitespace-nowrap">
                  {structure.name}
                </td>
                {MEDICATION_COLUMNS.map((col) => (
                  <td key={col.stockType} className="px-3 py-2.5 text-center">
                    <StatusBadge item={byType?.get(col.stockType)} />
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
